import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { ChecklistService, EventService, TaskService } from '../../../../services';
import { JobStatus } from '../../../../enums/job-status';
import { MarkerPoint, PauseMarkerPoint, PausePhotoMarker } from '../../../map-control/marker.point';
import moment from 'moment/moment';
import { MessageService, SelectItem } from 'primeng';
import { Checklist, ChecklistStep, StepType } from '../../../../interfaces/checklist';
import { IPauseTask } from '../../../../interfaces/pause-task';
import { OrganisationTypeEnum } from '../../../../enums/organisation-types';
import {
  JobScreenChecklistStep,
  JobScreenChecklistStepAnswerSection,
  JobScreenChecklistTask,
  SetChecklistStepCompleteRequest,
} from '../../../../interfaces/screen-specific/job-screen';
import { Subscription } from 'rxjs';
import { flatMap } from 'lodash';
import { ReadOnlyMode } from '../../../map-control/map-control.component';
import { TaskStepCategory } from '../../../../enums/task-step-category.enum';
import { Message } from '../../../../classes/Message';
import { RefreshJobEventTypeEnum } from '../../../../enums/refresh-job-event-type.enum';
import { NgxGalleryImage } from 'src/app/user-controls/ngx-gallery/ngx-gallery-image.model';
import { NgxGalleryOptions } from 'src/app/user-controls/ngx-gallery/ngx-gallery-options.model';
import { PausePhotoType } from '../../../../enums/pause-photo-type.enum';

@Component({
  selector: 'job-screen-checklist',
  templateUrl: './job-screen-checklist.component.html',
  styleUrls: ['./job-screen-checklist.component.scss'],
})
export class JobScreenChecklistComponent implements OnInit, OnChanges, OnDestroy {
  @Input('taskId') taskId!: number;
  @Input('organisationId') organisationId!: number;
  @Input('organisationTypeId') organisationTypeId!: number;

  task: JobScreenChecklistTask;

  public pauseReasons: SelectItem[] = [
    { value: 7, label: 'Awaiting Authorisation' },
    { value: 8, label: 'Awaiting Release' },
    { value: 1, label: 'Break' },
    { value: 10, label: 'Breakdown' },
    { value: 2, label: 'Overnight Parking' },
    { value: 3, label: 'Start Another Task' },
    { value: 9, label: 'Valet' },
    { value: 5, label: 'Vehicle Charging' },
    { value: 6, label: 'Vehicle Fuelling' },
  ];

  public OrganisationType = OrganisationTypeEnum;

  subscriptions: Subscription[] = [];

  public driverChecklistPickUp: JobScreenChecklistStep[] = [];
  public driverChecklistDropOff: JobScreenChecklistStep[] = [];
  isDriverChecklistManualEdit: boolean;
  JobStatus = JobStatus;
  selectedStep: ChecklistStep;
  currentLocation: MarkerPoint;
  showCurrentLocationDialog: boolean;

  pauseReasonId: null;
  showPauseReasonDialog: boolean;

  displaySteps: JobScreenChecklistStep[] = [];

  showToggleStepCompleteConfirm: boolean;
  stepToToggle: JobScreenChecklistStep;

  stepToPreview: JobScreenChecklistStep;

  pauses: PauseMarkerPoint[] = [];

  customStepPhotos: NgxGalleryImage[] = [];

  private readonly pausePhotoTypeNames: Record<PausePhotoType, string> = {
    [PausePhotoType.VehicleParking]: 'Vehicle Parking',
    [PausePhotoType.OverNightOdometer]: 'OverNight Odometer',
    [PausePhotoType.ResumeOdometer]: 'Resume Odometer',
    [PausePhotoType.Breakdown]: 'Breakdown',
  };

  customStepGalleryOptions: NgxGalleryOptions[] = [
    {
      lazyLoading: false,
      image: false,
      startIndex: 0,
      showPreview: true,
      previewZoom: true,
      preview: true,
      zoomInIcon: 'fa fa-plus',
      zoomOutIcon: 'fa fa-minus',
      rotateLeftIcon: 'fa fa-rotate-left',
      rotateRightIcon: 'fa fa-rotate-right',
      rotate: true,
      thumbnails: false,
      previewDescription: true,
    },
  ];

  public isSavingStep: boolean;

  protected readonly ReadOnlyMode = ReadOnlyMode;
  showEnhancedChecklistStepDialog: boolean = false;

  undoWarningStepTypes = [
    StepType.StartTask,
    StepType.PickupPhoto,
    StepType.PickupSignature,
    StepType.DropOffPhoto,
    StepType.DropOffSignature,
    StepType.StartMovement,
    StepType.Rating,
  ];

  public get showUndoWarning(): boolean {
    if (!this.stepToToggle) {
      return false;
    }

    return (
      !this.stepToToggle.isComplete &&
      this.undoWarningStepTypes.includes(this.stepToToggle.stepType)
    );
  }

  public get toggleCompleteHeader(): string {
    return this.stepToToggle?.isComplete ? 'Complete Step' : 'Undo Complete Step';
  }

  public get checklist(): Checklist {
    return this.task?.checklist;
  }

  public get showPauseResumeBtn(): boolean {
    return this.isJobPaused || this.jobStatus == JobStatus.Started;
  }

  public get checklistDisabledReason(): string | null {
    if (this.isJobCompleted && this.organisationTypeId != OrganisationTypeEnum.Engineius) {
      return 'Job is already completed';
    }

    if (!this.supplierAssigned) {
      return 'No supplier assigned';
    }

    return null;
  }

  public get manualEditDisabled(): boolean {
    return this.checklistDisabledReason != null;
  }

  public get showManualEditBtn(): boolean {
    return this.organisationTypeId == OrganisationTypeEnum.Engineius;
  }

  public get jobStatus(): number | null {
    return this.task?.jobStatusId;
  }

  public get isJobCompleted(): boolean {
    return (
      this.jobStatus == JobStatus.Completed ||
      this.jobStatus == JobStatus.Cancelled ||
      this.jobStatus == JobStatus.Closed
    );
  }

  public get jobId(): number {
    return this.task?.jobId;
  }

  public get isJobPaused(): boolean {
    return this.jobStatus == JobStatus.Paused;
  }

  constructor(
    private taskService: TaskService,
    private messageService: MessageService,
    private checklistService: ChecklistService,
    private eventService: EventService,
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['taskId']) {
      void this.loadTask(this.taskId);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((x) => x?.unsubscribe());
  }

  ngOnInit() {
    this.subscriptions.push(
      this.eventService.JobScreenEvent.subscribe((event: any) => {
        this.processEvents(event);
      }),
    );
  }

  private processEvents(event: any) {
    if (event.event == 'refresh-supplier') {
      void this.loadTask(this.taskId);
    }
  }

  checklistManualEdit() {
    this.isDriverChecklistManualEdit = !this.isDriverChecklistManualEdit;

    if (!this.isDriverChecklistManualEdit) {
      return;
    }

    void this.loadTask(this.taskId);
  }

  onPauseResumeTask(pauseStatus: boolean) {
    this.pauseReasonId = null;

    if (!pauseStatus) {
      this.showPauseReasonDialog = true;
    } else {
      this.pauseResumeTask();
    }
  }

  stepCompleteToggled(step: JobScreenChecklistStep) {
    this.stepToToggle = step;

    if (!step.isComplete && !this.showUndoWarning) {
      void this.saveStepCompleted(step);
      return;
    }

    this.showToggleStepCompleteConfirm = true;
  }

  handleShowCurrentLocation(step: JobScreenChecklistStep) {
    this.selectedStep = step;
    this.currentLocation = new MarkerPoint();
    this.currentLocation.longitude = this.selectedStep.longitude;
    this.currentLocation.latitude = this.selectedStep.latitude;
    this.showCurrentLocationDialog = true;
  }

  toggleStepDescription(step: JobScreenChecklistStep) {
    step.showDescription = !step.showDescription;
  }

  canToggle(step: any) {
    const element = document.getElementById('stepDescription-' + step.id);

    return element.scrollHeight > 18;
  }

  showEnhancedStepDetails(step: JobScreenChecklistStep) {
    this.stepToPreview = step;
    this.showEnhancedChecklistStepDialog = true;
  }

  pauseResumeTask() {
    //get the last task step

    let pauseTask: IPauseTask = {
      id: 0,
      isResume: this.isJobPaused,
      pauseReasonId: this.pauseReasonId,
      createdOn: moment(moment().format('YYYY-MM-DD HH:mm:ss')).toDate(),
      latitude: null,
      longitude: null,
    };

    //create a pause reason and a pause object
    if (pauseTask.isResume) {
      this.taskService
        .resumeTask(this.jobId, this.taskId, pauseTask)
        .then(() => {
          void this.loadTask(this.taskId);
          this.eventService.JobScreenEvent.emit({
            event: 'refresh-order',
            sender: 'job-screen-checklist',
          });
        })
        .catch(() => {});
    } else {
      this.taskService
        .pauseTask(this.jobId, this.taskId, pauseTask)
        .then(() => {
          this.showPauseReasonDialog = false;
          void this.loadTask(this.taskId);
          this.eventService.JobScreenEvent.emit({
            event: 'refresh-order',
            sender: 'job-screen-checklist',
          });
        })
        .catch(() => {
          this.showPauseReasonDialog = false;
        });
    }
  }

  async loadTask(taskId: number) {
    this.task = await this.taskService.getForComponent<JobScreenChecklistTask>(
      taskId,
      'job-screen-checklist',
    );
    this.pauses = this.task.pauses;
    this.displaySteps = this.getDriverCheckList(this.checklist);
    this.driverChecklistPickUp = this.displaySteps.filter((x) => x.isPickup);
    this.driverChecklistDropOff = this.displaySteps.filter((x) => !x.isPickup);
  }

  getDriverCheckList(checklist: Checklist): JobScreenChecklistStep[] {
    // If task or steps are falsy, return no steps, makes sense.
    if (!checklist || !checklist.steps) {
      return [];
    }

    let displaySteps: JobScreenChecklistStep[] = [];

    checklist.steps.forEach((step) => {
      let answerSections: JobScreenChecklistStepAnswerSection[] = [];

      let isEnhancedStep = false;

      if (step.customStepConfig?.freeTextConfig) {
        let label = step.customStepConfig?.freeTextConfig.isMandatory
          ? 'Free Text Response (Required)'
          : 'Free Text Response';
        answerSections.push({ label, text: step.customStepConfig.freeTextConfig.value });
        isEnhancedStep = true;
      }

      if (step.customStepConfig?.photoUploadConfig) {
        let label = `Photo Response (${step.customStepConfig?.photoUploadConfig.minimumPhotosRequired} Required)`;
        answerSections.push({ label, photos: step.stepPhotos.map((x) => x.location) });
        isEnhancedStep = true;
      }

      if (step.customStepConfig?.multiChoiceConfig?.options) {
        answerSections.push({
          label: 'Multi Choice Response',
          options: step.customStepConfig.multiChoiceConfig.options,
        });
        isEnhancedStep = true;
      }

      let newStep: JobScreenChecklistStep = {
        ...step,
        isComplete: step.completedDateTime != null,
        answerSections: answerSections,
        showCheckbox: true,
        isEnhancedStep,
      };

      newStep.isComplete = step.completedDateTime != null;

      displaySteps.push(newStep);

      const pauseSteps = this.pauses.filter((x) => x.stepId == step.id);

      const pauseStepsAsChecklistSteps = flatMap(pauseSteps, (pause) =>
        this.convertToChecklistSteps(pause, step),
      );

      displaySteps.push(...pauseStepsAsChecklistSteps);
    });

    return displaySteps;
  }

  private convertToChecklistSteps(
    pause: PauseMarkerPoint,
    step: ChecklistStep,
  ): JobScreenChecklistStep[] {
    const result: JobScreenChecklistStep[] = [
      {
        id: 0,
        title: pause.name,
        description: '',
        isPickup: step.isPickup,
        isCustomStep: false,
        stepType: StepType.Paused,
        completedDateTime: pause.createdOn,
        signatures: [],
        customStepConfig: null,
        latitude: pause.latitude,
        longitude: pause.longitude,
        stepPhotos: [],
        isEnhancedStep: false,
        showDescription: false,
        isComplete: true,
        showCheckbox: false,
        answerSections: [],
        pausePhotos: pause.photos ?? [],
      },
    ];

    if (pause.resumeInfo) {
      result.push({
        id: 0,
        title: pause.resumeInfo.name,
        description: '',
        isPickup: step.isPickup,
        isCustomStep: false,
        stepType: StepType.Paused,
        completedDateTime: pause.resumeInfo.createdOn,
        signatures: [],
        customStepConfig: null,
        latitude: pause.latitude,
        longitude: pause.longitude,
        stepPhotos: [],
        isEnhancedStep: false,
        showDescription: false,
        isComplete: true,
        showCheckbox: false,
        answerSections: [],
        pausePhotos: pause.resumeInfo.photos ?? [],
      });
    }

    return result;
  }

  public get supplierAssigned(): boolean {
    return this.task.jobSupplierId != null;
  }

  async saveStepCompleted(stepToToggle: JobScreenChecklistStep) {
    const request: SetChecklistStepCompleteRequest = {
      taskStepId: stepToToggle.id,
      taskId: this.taskId,
      isCustomStep: false,
      isOrganisationStep: stepToToggle.isCustomStep,
      jobId: this.jobId,
      date: stepToToggle.isComplete
        ? moment(stepToToggle.completedDateTime).local().format()
        : null,
      isComplete: stepToToggle.isComplete,
    };

    this.isSavingStep = true;

    try {
      await this.checklistService.completeTaskStep(this.taskId, stepToToggle.id, request);
      this.messageService.add(Message.Success('Step Updated', 'Success'));
      this.showToggleStepCompleteConfirm = false;
      this.eventService.RefreshIncidentsEvent.emit();
      this.eventService.RefreshJobEvent.emit(RefreshJobEventTypeEnum.RefreshHistory);
      this.eventService.JobScreenEvent.emit({
        event: 'refresh-order',
        sender: 'job-screen-checklist',
      });
      this.eventService.JobScreenEvent.emit({
        event: 'job-details-changed',
        sender: 'job-screen-checklist',
      });
      void this.loadTask(this.taskId);
    } catch (error) {
      this.messageService.add(Message.Error('Failed to update step', 'Error'));
    } finally {
      this.isSavingStep = false;
    }
  }

  asChecklistStep(stepUntyped: any): JobScreenChecklistStep {
    return stepUntyped as JobScreenChecklistStep;
  }

  onToggleCancel(step: JobScreenChecklistStep) {
    this.showToggleStepCompleteConfirm = false;

    step.isComplete = !step.isComplete;

    if (!step.isComplete) {
      step.completedDateTime = null;
    }
  }

  clearCustomStepPhoto() {
    this.customStepPhotos = [];
    this.setGalleryStartIndex(0);
  }

  openPhoto(publicUrl: string) {
    const extension = 'jpg';
    this.setGalleryStartIndex(0);
    this.customStepPhotos.push({
      url: publicUrl,
      extension,
      big: publicUrl,
      medium: publicUrl,
      small: publicUrl,
      leftIcon: null,
    });
  }

  // A pause can have more than one photo, so open the whole set at the icon that was clicked
  openPausePhotos(photos: PausePhotoMarker[], index: number) {
    this.customStepPhotos = photos.map((photo) => ({
      url: photo.location,
      extension: photo.location?.split('.').pop() ?? 'jpg',
      big: photo.location,
      medium: photo.location,
      small: photo.location,
      leftIcon: null,
      descriptionData: {
        areaTitle: this.pausePhotoTypeNames[photo.photoType] ?? 'Pause Photo',
        dateTime: photo.dateTime ? new Date(photo.dateTime) : null,
        isSignature: false,
      },
    }));

    this.setGalleryStartIndex(index);
  }

  private setGalleryStartIndex(index: number) {
    if (this.customStepGalleryOptions.length > 0) {
      this.customStepGalleryOptions[0].startIndex = index;
    }
  }
}
